<template>
    <vx-modal v-model:visible="visible" size="md">
        <template v-slot:header>{{ props.action === 'suspend' ? 'Suspend User' : 'Unsuspend User' }}</template>

        <div v-if="props.action === 'suspend'" class="space-y-4">
            <vx-label size="md">Why is this user being suspended?</vx-label>
            <vx-radio-selection size="sm" v-model="selectedReason">
                <vx-radio-selection-item value="Security Concern">Security Concern</vx-radio-selection-item>
                <vx-radio-selection-item value="Left Company">Left Company</vx-radio-selection-item>
                <vx-radio-selection-item value="custom">Custom</vx-radio-selection-item>
            </vx-radio-selection>

            <div v-if="selectedReason === 'custom'" class="mt-4">
                <vx-label size="md">Custom Reason</vx-label>
                <vx-input
                    v-model="reason"
                    size="lg"
                    :rows="4"
                    maxlength="250"
                    data-test="suspend-reason-input"
                ></vx-input>
            </div>

            <vx-button
                @click="handleSuspend"
                :loading="submitting"
                :disabled="!reason"
                size="lg"
                color="danger"
                class="w-full"
                data-test="suspend-user-button"
            >
                Suspend User
            </vx-button>

            <div class="text-xs text-gray-500 text-center">
                The user will be removed from all teams, their password, API tokens, mobile devices and web sessions
                will be revoked, and they will be logged out everywhere.
            </div>
        </div>

        <div v-else class="space-y-4">
            <div class="text-sm text-slate-700">
                The user will be able to log in again once they reset their password or log in with SSO. Their team
                memberships are not restored: re-add them with the accept-invite command, a CSV user import, or a new
                invitation.
            </div>

            <vx-button
                @click="handleUnsuspend"
                :loading="submitting"
                size="lg"
                color="primary"
                class="w-full"
                data-test="unsuspend-user-button"
            >
                Unsuspend User
            </vx-button>
        </div>
    </vx-modal>
</template>

<script setup>
import {
    VxButton,
    VxInput,
    VxLabel,
    VxModal,
    VxRadioSelection,
    VxRadioSelectionItem,
} from '@voxie/frontend-components';
import { ref, watch } from 'vue';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import { useToasted } from '~/composables/useToasted';
import { adminUsersClient } from '~/services';

const props = defineProps({
    action: {
        type: String,
        required: true,
    },
});

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const user = defineModel('user', {
    type: Object,
    required: true,
});

const toasted = useToasted();

const selectedReason = ref('');
const reason = ref('');
const submitting = ref(false);

watch(selectedReason, () => {
    reason.value = selectedReason.value === 'custom' ? '' : selectedReason.value;
});

watch(visible, (newValue) => {
    if (!newValue) {
        selectedReason.value = '';
        reason.value = '';
    }
});

const handleError = (e) => {
    console.error(e);
    if (e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
        toasted.global.platform_error();
        return;
    }
    toasted.error(e.response?.data?.message || 'Something went wrong.');
};

const handleSuspend = async () => {
    if (submitting.value || !reason.value) {
        return;
    }

    submitting.value = true;

    try {
        const response = await adminUsersClient.suspendUser(user.value.id, { reason: reason.value });
        user.value.suspended_at = response.data.suspended_at;
        user.value.suspension_reason = response.data.suspension_reason;
        user.value.current_team_id = null;
        user.value.current_team_name = null;
        toasted.success('User suspended successfully');
        visible.value = false;
    } catch (e) {
        handleError(e);
    } finally {
        submitting.value = false;
    }
};

const handleUnsuspend = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        await adminUsersClient.unsuspendUser(user.value.id);
        user.value.suspended_at = null;
        user.value.suspension_reason = null;
        toasted.success('User unsuspended successfully');
        visible.value = false;
    } catch (e) {
        handleError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
